// File: frontend/src/components/AdvancedIOCHandler.tsx
import React from 'react';
import useSWR from 'swr';
import { useApi } from '../hooks/useApi';
import {
    CircularProgress,
    Alert,
    Box,
    Button,
    Typography
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import HybridIOCTable from './HybridIOCTable';
import { HybridIOC_V2 } from '../types';

interface AdvancedIOCHandlerProps {
    url: string | null | undefined;
    reportId: string; // reportId is used for the downloaded filename
}

const AdvancedIOCHandler: React.FC<AdvancedIOCHandlerProps> = ({ url, reportId }) => {
    const { simpleTextFetcher } = useApi();
    // Fetch the content of the JSON blob as a string
    const { data, error, isLoading } = useSWR(url, simpleTextFetcher);

    const handleDownload = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault(); // Prevent any default browser action
        if (!data) return;
        const blob = new Blob([data], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `advanced_iocs_${reportId}.json`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href); // Clean up the object URL
    };

    if (!url) {
        return <Alert severity="info" sx={{ my: 1 }}>No advanced IOC data available for this report.</Alert>;
    }

    if (isLoading) {
        return <CircularProgress size={24} />;
    }

    if (error) {
        return <Alert severity="error" sx={{ my: 1 }}>Could not load advanced IOC content.</Alert>;
    }

    // Always render the layout with the button, but the table conditionally
    let iocs: HybridIOC_V2[] = [];
    if (data) {
        try {
            iocs = JSON.parse(data);
        } catch (e) {
            console.error("Failed to parse advanced IOC data:", e);
            return <Alert severity="error">Failed to parse advanced IOC data.</Alert>;
        }
    }

    const filteredIocs = iocs.filter(
        (ioc) => ioc.confidence === 'high' || ioc.confidence === 'medium'
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
                <Typography variant="h6">
                    Advanced IOCs
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    disabled={!data} // Button is disabled until data is loaded
                >
                    Download Advanced IOCs (JSON)
                </Button>
            </Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Note:</strong> A STIX-formatted version of these indicators may also be available. You can import the <code>advanced_iocs_stix.json</code> file (found in the "STIX" tab) into your Threat Intelligence Platform (TIP) or other security tools that support STIX 2.1.
            </Alert>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Displaying High & Medium Confidence IOCs
            </Typography>
            {filteredIocs.length > 0 ? (
                <HybridIOCTable iocs={filteredIocs} />
            ) : (
                <Alert severity="info">No IOCs with high or medium confidence were found. The downloaded file may contain low-confidence IOCs.</Alert>
            )}
        </Box>
    );
};

export default AdvancedIOCHandler;